import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { useAuth } from './AuthContext'
import { setAudioDevMuted } from '../hooks/useAudioAlerts'
import { apiPut } from '../utils/api'
import { isOnCallInDevMode } from '../utils/onCall'

/**
 * App-wide MODE.
 *
 *  • 'support' — the normal platform (Support + CRM nav, notifications, rings).
 *  • 'dev'     — a stripped-down blank workspace. All Support/CRM nav and features
 *                are hidden, and NO notifications / rings fire (the components that
 *                produce them live in the support Layout, which isn't mounted in
 *                dev mode).
 *
 * Persisted in sessionStorage (PER-TAB) so it survives a reload of that tab but is
 * independent between tabs — tab 1 can be in Support while tab 2 is in Dev, and
 * refreshing either keeps its own mode (localStorage was shared across tabs, so a
 * refresh used to inherit whatever the other tab last set). Switching is always
 * guarded by a confirmation dialog so it never happens by accident.
 *
 * MVP scaffold — see docs/DEV-MODE-HANDOFF.md for the remaining work.
 */
export type AppMode = 'support' | 'dev'

interface AppModeCtx {
  mode: AppMode
  /** Ask to switch modes — shows a confirmation dialog first. */
  requestSwitch: (target: AppMode) => void
  /** Switch straight to Support with NO dialog — used when an on-call agent
   *  picks up an incoming chat from Dev Mode (Lee, 4 Sep). */
  forceSupport: () => void
}

const Ctx = createContext<AppModeCtx>({ mode: 'support', requestSwitch: () => {}, forceSupport: () => {} })
export const useAppMode = () => useContext(Ctx)

const STORAGE_KEY = 'wq_app_mode'
// PER-TAB: sessionStorage, not localStorage — each tab keeps its own mode across
// refreshes without leaking into other tabs.
function readMode(): AppMode {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === 'dev' ? 'dev' : 'support'
  } catch {
    return 'support'
  }
}

export const AppModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  const { isAuthenticated, currentAgent } = useAuth()
  const [mode, setModeState] = useState<AppMode>(readMode)
  const [pending, setPending] = useState<AppMode | null>(null)


  // Dev-ONLY accounts (Lee, 26 Aug 2026): locked to the Dev Board — always in
  // dev mode, always on /dev, no switch to Support. Enforced on every auth/mode
  // change so even a hand-edited localStorage can't escape it.
  // (Lee, Sep 2026): everyone can now access BOTH Support and Dev — the previous
  // "dev-only" hard-lock to Dev Mode has been retired, so no account is trapped
  // on one side. The `dev` realRole still LANDS in Dev on login (below), but can
  // switch freely.

  // Dev accounts land in DEV MODE on login (Lee, 29 Jul 2026) — once per login session;
  // they can switch to Support afterwards via the normal toggle.
  useEffect(() => {
    if (!isAuthenticated || (currentAgent as any)?.realRole !== 'dev') return
    try {
      if (sessionStorage.getItem('wq_dev_landing_done') === '1') return
      sessionStorage.setItem('wq_dev_landing_done', '1')
    } catch { /* storage unavailable — land anyway */ }
    setModeState('dev')
    try { sessionStorage.setItem(STORAGE_KEY, 'dev') } catch { /* no-op */ }
    navigate('/dev')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, currentAgent])

  // Enforce the mode's notification policy at the SOURCE (not just via which
  // components happen to be mounted):
  //   • setAudioDevMuted() hard-silences all dashboard audio + desktop
  //     notifications while in Dev Mode.
  //   • The server flag (best-effort) makes pushService skip this agent's
  //     MOBILE push in Dev Mode, and reliably clears it in Support Mode.
  // Runs on mount and on every mode/auth change, so switching back to Support
  // (or logging in fresh on any device) always restores normal alerting.
  // On-call agents (utils/onCall.ts) are NOT silenced by Dev Mode: their
  // incoming-chat ringer keeps ringing there, and their mobile push stays on.
  const onCall = isOnCallInDevMode(currentAgent)
  useEffect(() => {
    setAudioDevMuted(mode === 'dev' && !onCall)
    if (!isAuthenticated) return
    apiPut('/agents/dev-mode', { devMode: mode === 'dev' && !onCall }).catch(() => {
      /* best-effort — the client-side gate above already guarantees silence */
    })
  }, [mode, isAuthenticated, onCall])

  const setMode = useCallback((m: AppMode) => {
    setModeState(m)
    try { sessionStorage.setItem(STORAGE_KEY, m) } catch { /* ignore */ }
  }, [])

  const forceSupport = useCallback(() => {
    setMode('support')
  }, [setMode])

  const requestSwitch = useCallback((target: AppMode) => {
    setPending((cur) => (target === mode ? cur : target))
  }, [mode])

  const confirm = () => {
    if (!pending) return
    const target = pending
    setPending(null)
    setMode(target)
    navigate(target === 'dev' ? '/dev' : '/dashboard', { replace: true })
  }

  return (
    <Ctx.Provider value={{ mode, requestSwitch, forceSupport }}>
      {children}
      {pending &&
        createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setPending(null) }}
          >
            <div className="w-full max-w-sm rounded-xl bg-surface border border-border shadow-float p-5">
              <h3 className="text-base font-semibold text-txt-primary">
                {pending === 'dev' ? 'Switch to Dev Mode?' : 'Switch to Support Mode?'}
              </h3>
              <p className="text-sm text-txt-secondary mt-1.5">
                {pending === 'dev'
                  ? 'This hides all Support and CRM menus and features, and silences all notifications and rings until you switch back.'
                  : 'This returns you to the full Support and CRM platform, with notifications and rings back on.'}
              </p>
              <div className="flex justify-end gap-2 mt-5">
                <button
                  onClick={() => setPending(null)}
                  className="px-3.5 py-1.5 rounded-lg text-sm font-medium text-txt-secondary hover:bg-surface-hover transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirm}
                  className="px-3.5 py-1.5 rounded-lg text-sm font-bold text-white bg-brand-600 hover:bg-brand-700 transition-colors"
                >
                  {pending === 'dev' ? 'Enter Dev Mode' : 'Enter Support Mode'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </Ctx.Provider>
  )
}
